import Vue from 'vue'
import {
  extend,
  configure,
  ValidationObserver,
  ValidationProvider,
} from 'vee-validate'

import {
  required,
  min,
  max,
  is,
  email,
  confirmed,
  between,
  oneOf,
} from 'vee-validate/dist/rules'

import { i18n } from '@plugins/i18n'
import {
  checkIsValidDate,
  checkDateRanger,
  checkDateFormat,
  checkDateTimeRanger,
} from '@utils/format-date'
import isBefore from 'date-fns/isBefore'

// Install VeeValidate components globally
Vue.component('ValidationObserver', ValidationObserver)
Vue.component('ValidationProvider', ValidationProvider)

configure({
  events: '',
  defaultMessage: (field, values) => {
    // override the field name.
    values._field_ = i18n.t(`fields.${field}`)
    return i18n.t(`validation.${values._rule_}`, values)
  },
})

extend('scope', {
  validate(value, { min, max }) {
    return value.length >= min && value.length <= max
  },
  params: ['min', 'max'],
})
/**
 * validate between with character
 * need custom because between rules vee-validate just validate number
 */
extend('minmax', {
  validate(value, { min, max }) {
    return value.length >= min && value.length <= max
  },
  params: ['min', 'max'],
})

/**
 * custom validate password
 */
extend('password', {
  validate(value) {
    if (value === ' ') {
      return false
    }
    return new RegExp(/^[\x21-\x7E]{6,16}$/).test(value)
  },
})

/**
 * custom validate phone number
 */
extend('phone', {
  validate(value) {
    if (value.charAt(0) === '-' || value.charAt(value.length - 1) === '-') {
      return false
    }
    return new RegExp(/^[0-9-]{10,13}$/).test(value)
  },
})

/**
 * custom validate mac add
 */
extend('mac', {
  validate(value) {
    return new RegExp(/^([0-9A-F]{2}[:]){5}([0-9A-F]{2})$/).test(value)
  },
})

extend('adminID', {
  validate(value) {
    return new RegExp(/^[A-Za-z0-9]{6,30}$/).test(value)
  },
})
/**
 * custom check date is valid
 */
extend('dateFormat', {
  validate(value, { format }) {
    if (format === true) {
      return checkIsValidDate(value)
    } else {
      return checkDateFormat(value, format)
    }
  },
  params: ['format'],
})

/**
 * custom check date ranger valid
 */
extend('dateRanger', {
  validate(value, { startDate, endDate }) {
    return checkDateRanger(startDate, value)
  },
  params: ['startDate', 'endDate'],
})

extend('timeBeforeNow', {
  validate(value) {
    if (!checkIsValidDate(value)) {
      return
    }
    return isBefore(new Date(value), new Date())
  },
})

extend('timeAfterNow', {
  validate(value) {
    if (!checkIsValidDate(value)) {
      return
    }
    return !isBefore(new Date(value), new Date())
  },
})

extend('endTimeBeyondStatTime', {
  validate(value, { startDate }) {
    if (!startDate || !value) {
      return true
    }
    return checkDateTimeRanger(startDate, value)
  },
  params: ['startDate'],
})

extend('endTimeBeyondStatTimeDownloadCSV', {
  validate(value, { startDate }) {
    if (!startDate || !value) {
      return true
    }
    return checkDateRanger(startDate, value)
  },
  params: ['startDate'],
})

extend('requiredIfDate', {
  validate(value, { target }) {
    if (!target) {
      return true
    }
    return {
      required: true,
      valid: ['', null, undefined].indexOf(value) === -1,
    }
  },
  params: ['target'],
  computesRequired: true,
})

extend('requiredWithFormat', {
  validate(value) {
    return ['', null, undefined].indexOf(value) === -1
  },
  computesRequired: true,
})

extend('requiredIf', {
  validate(value, { target }) {
    if (['', null, undefined].indexOf(target) !== -1) {
      return true
    }
    return ['', null, undefined].indexOf(value) === -1
  },
  params: ['target'],
  computesRequired: true,
})

/**
 * custom number ranger valid
 */
extend('numberRange', {
  validate(value, { min, max }) {
    if (
      isNaN(min) ||
      isNaN(max) ||
      ['', null, undefined].indexOf(min) !== -1 ||
      ['', null, undefined].indexOf(max) !== -1
    ) {
      return true
    }
    if (!isNaN(value)) {
      return (
        parseInt(value, 10) >= parseInt(min, 10) &&
        parseInt(value, 10) <= parseInt(max, 10)
      )
    }
    return !value
  },
  params: ['min', 'max'],
})

/**
 * custom value ranger valid
 */
extend('valueRange', {
  validate(value, { min, max }) {
    if (isNaN(Number(min)) || isNaN(Number(max))) {
      return true
    }
    if (!isNaN(Number(value))) {
      return (
        parseInt(value, 10) >= parseInt(min, 10) &&
        parseInt(value, 10) <= parseInt(max, 10)
      )
    }
    return !value
  },
  params: ['min', 'max'],
})

extend('imgExt', {
  validate(files, { ext }) {
    const regex = new RegExp(`.(${ext.join('|')})$`, 'i')
    if (Array.isArray(files)) {
      return files.every((file) => regex.test(file.name))
    }

    return regex.test(files.name)
  },
  params: ['ext'],
})

// Install required rule and message.
extend('required', required)

// Install email rule and message.
extend('email', email)

// Install min rule and message.
extend('min', min)

// Install max rule and message.
extend('max', max)

// Install is rule and message.
extend('is', is)

// Install confirmed rule and message.
extend('confirmed', confirmed)

// Install between rule and message.
extend('between', between)

// Install oneOf rule and message.
extend('oneOf', oneOf)
